import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ProjectOverviewResponseDto } from '../models/project-overview.dto';
import {
  SiteRequestDto,
  SiteResponseDto,
  SitesListResponseDto,
  SiteTicketsResponseDto,
  SiteTableColumnsListResponseDto,
  SiteTableColumnResponseDto,
  SiteTableColumnRequestDto,
  SiteTableColumnUpdateRequestDto,
  SiteTableColumnOptionRequestDto,
  SiteTableColumnOptionUpdateRequestDto,
  SiteTableColumnOptionResponseDto,
  SiteCustomFieldValueResponseDto,
} from '../models/site.dto';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import {
  ProjectRequestDto,
  ProjectResponseDto,
  ProjectMembersListResponseDto,
  ProjectMemberRequestDto,
  ProjectMemberResponseDto,
  ProjectInviteByEmailRequestDto,
  ProjectLinkSettingsResponseDto,
  ProjectLinkSettingsRequestDto,
  ProjectMembersDetailListResponseDto,
  ProjectMemberRoleResponseDto,
  JoinProjectRequestDto,
  JoinProjectResponseDto,
} from '../models/project.dto';
import { RolesListResponseDto } from '../models/role.dto';
import { ProjectDashboardResponseDto } from '../models/project-dashboard.dto';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  private readonly apiUrl = `${environment.apiHost}/api`;

  // Per-project response caches: repeat visits to the same project within a session reuse
  // the last response instead of refetching; mutations below invalidate the relevant entries.
  private readonly projectCache = new Map<string, Observable<ProjectResponseDto>>();
  private readonly sitesCache = new Map<string, Observable<SitesListResponseDto>>();
  private readonly membersCache = new Map<string, Observable<ProjectMembersListResponseDto>>();

  constructor(private readonly http: HttpClient) { }

  // The HTTP interceptor will automatically add the authorization token
  public getProject(projectId: string): Observable<ProjectResponseDto> {
    let cached = this.projectCache.get(projectId);
    if (!cached) {
      cached = this.http.get<ProjectResponseDto>(`${this.apiUrl}/projects/${projectId}/`).pipe(
        catchError((err) => {
          this.projectCache.delete(projectId);
          return throwError(() => err);
        }),
        shareReplay(1)
      );
      this.projectCache.set(projectId, cached);
    }
    return cached;
  }

  public getProjectOverview(projectId: string): Observable<ProjectOverviewResponseDto> {
    return this.http.get<ProjectOverviewResponseDto>(`${this.apiUrl}/projects/${projectId}/overview/`);
  }

  /**
   * Per-project dashboard: assigned-to-me, pending-my-approval and requests grouped by status.
   * No caching — poll/refetch after ticket mutations, there's no websocket push for this yet.
   */
  public getProjectDashboard(projectId: string): Observable<ProjectDashboardResponseDto> {
    return this.http.get<ProjectDashboardResponseDto>(`${this.apiUrl}/projects/${projectId}/dashboard/`);
  }

  public createProject(project: ProjectRequestDto): Observable<ProjectResponseDto> {
    return this.http.post<ProjectResponseDto>(`${this.apiUrl}/projects/`, project);
  }

  public updateProject(project: ProjectRequestDto, projectId: string): Observable<ProjectResponseDto> {
    return this.http.put<ProjectResponseDto>(`${this.apiUrl}/projects/${projectId}/`, project).pipe(
      tap(() => this.projectCache.delete(projectId))
    );
  }

  /** Partial update — e.g. { name }, { logo }, { default_assignee_id }. */
  public patchProject(projectId: string, body: Record<string, unknown>): Observable<ProjectResponseDto> {
    return this.http.patch<ProjectResponseDto>(`${this.apiUrl}/projects/${projectId}/`, body).pipe(
      tap(() => this.projectCache.delete(projectId))
    );
  }

  /** Permanently deletes the project and cascades: members, tickets, sites, integrations. Admin-only, irreversible. */
  public deleteProject(projectId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/projects/${projectId}/`).pipe(
      tap(() => this.projectCache.delete(projectId))
    );
  }

  /** Removes the caller's own membership from the project. Any role can call it; 403s if they're the sole admin. */
  public leaveProject(projectId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/projects/${projectId}/leave/`).pipe(
      tap(() => this.projectCache.delete(projectId))
    );
  }

  public createSite(site: SiteRequestDto): Observable<SiteResponseDto> {
    return this.http.post<SiteResponseDto>(`${this.apiUrl}/sites/`, site).pipe(
      tap(() => this.sitesCache.clear())
    );
  }

  public getProjectSites(projectId: string): Observable<SitesListResponseDto> {
    let cached = this.sitesCache.get(projectId);
    if (!cached) {
      cached = this.http.get<SitesListResponseDto>(`${this.apiUrl}/projects/${projectId}/sites/`).pipe(
        catchError((err) => {
          this.sitesCache.delete(projectId);
          return throwError(() => err);
        }),
        shareReplay(1)
      );
      this.sitesCache.set(projectId, cached);
    }
    return cached;
  }

  public updateSite(siteId: string, site: Partial<SiteRequestDto>): Observable<SiteResponseDto> {
    return this.http.patch<SiteResponseDto>(`${this.apiUrl}/sites/${siteId}/`, site).pipe(
      tap(() => this.sitesCache.clear())
    );
  }

  public deleteSite(siteId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/sites/${siteId}/`).pipe(
      tap(() => this.sitesCache.clear())
    );
  }

  public getSiteTickets(siteId: string): Observable<SiteTicketsResponseDto> {
    return this.http.get<SiteTicketsResponseDto>(`${this.apiUrl}/sites/${siteId}/request-tickets/`);
  }

  /** GET /api/projects/{project_id}/site-table-columns/ — custom columns a project has added to its sites table. */
  public getSiteTableColumns(projectId: string): Observable<SiteTableColumnsListResponseDto> {
    return this.http.get<SiteTableColumnsListResponseDto>(`${this.apiUrl}/projects/${projectId}/site-table-columns/`);
  }

  /** POST /api/projects/{project_id}/site-table-columns/ — body: { label, type }. Appended at the end. */
  public createSiteTableColumn(projectId: string, body: SiteTableColumnRequestDto): Observable<SiteTableColumnResponseDto> {
    return this.http.post<SiteTableColumnResponseDto>(`${this.apiUrl}/projects/${projectId}/site-table-columns/`, body).pipe(
      tap(() => this.sitesCache.clear())
    );
  }

  /** PATCH /api/site-table-columns/{id}/ — rename/resize/reorder. Send only the field(s) being changed. */
  public updateSiteTableColumn(columnId: string, body: SiteTableColumnUpdateRequestDto): Observable<SiteTableColumnResponseDto> {
    return this.http.patch<SiteTableColumnResponseDto>(`${this.apiUrl}/site-table-columns/${columnId}/`, body);
  }

  /** DELETE /api/site-table-columns/{id}/ — cascades its options and per-site custom values. */
  public deleteSiteTableColumn(columnId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/site-table-columns/${columnId}/`).pipe(
      tap(() => this.sitesCache.clear())
    );
  }

  /** POST /api/site-table-columns/{id}/options/ — body: { label, color, background }. */
  public addSiteTableColumnOption(
    columnId: string,
    body: SiteTableColumnOptionRequestDto
  ): Observable<SiteTableColumnOptionResponseDto> {
    return this.http.post<SiteTableColumnOptionResponseDto>(`${this.apiUrl}/site-table-columns/${columnId}/options/`, body);
  }

  /** PATCH /api/site-table-columns/{columnId}/options/{optionId}/ — rename/recolor/reorder. Send only the field(s) being changed. */
  public updateSiteTableColumnOption(
    columnId: string,
    optionId: string,
    body: SiteTableColumnOptionUpdateRequestDto
  ): Observable<SiteTableColumnOptionResponseDto> {
    return this.http
      .patch<SiteTableColumnOptionResponseDto>(`${this.apiUrl}/site-table-columns/${columnId}/options/${optionId}/`, body)
      .pipe(tap(() => this.sitesCache.clear()));
  }

  /** DELETE /api/site-table-columns/{columnId}/options/{optionId}/ — clears the option from any site currently set to it. */
  public deleteSiteTableColumnOption(columnId: string, optionId: string): Observable<any> {
    return this.http
      .delete<any>(`${this.apiUrl}/site-table-columns/${columnId}/options/${optionId}/`)
      .pipe(tap(() => this.sitesCache.clear()));
  }

  /** PATCH /api/sites/{site_id}/custom-fields/{column_id}/ — upsert one cell's custom-column value. */
  public setSiteCustomFieldValue(siteId: string, columnId: string, value: any): Observable<SiteCustomFieldValueResponseDto> {
    return this.http
      .patch<SiteCustomFieldValueResponseDto>(`${this.apiUrl}/sites/${siteId}/custom-fields/${columnId}/`, { value })
      .pipe(tap(() => this.sitesCache.clear()));
  }

  public getProjectMembers(projectId: string): Observable<ProjectMembersListResponseDto> {
    let cached = this.membersCache.get(projectId);
    if (!cached) {
      cached = this.http.get<ProjectMembersListResponseDto>(`${this.apiUrl}/projects/${projectId}/members/`).pipe(
        catchError((err) => {
          this.membersCache.delete(projectId);
          return throwError(() => err);
        }),
        shareReplay(1)
      );
      this.membersCache.set(projectId, cached);
    }
    return cached;
  }

  public addProjectMember(member: ProjectMemberRequestDto): Observable<ProjectMemberResponseDto> {
    return this.http.post<ProjectMemberResponseDto>(`${this.apiUrl}/projectmembers/`, member).pipe(
      tap(() => this.membersCache.clear())
    );
  }

  /**
   * Invite a user to the project by email (e.g. from another organization).
   * POST /api/projects/{project_id}/invite/
   * Body: { email, project_role? } — project_role is the unified role name (admin | member | viewer).
   * Only project admins can call this endpoint.
   */
  public inviteProjectMemberByEmail(projectId: string, body: ProjectInviteByEmailRequestDto): Observable<ProjectMemberResponseDto> {
    return this.http.post<ProjectMemberResponseDto>(`${this.apiUrl}/projects/${projectId}/invite/`, body).pipe(
      tap(() => this.membersCache.delete(projectId))
    );
  }

  public deleteProjectMember(projectMemberId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/projectmembers/${projectMemberId}/`).pipe(
      tap(() => this.membersCache.clear())
    );
  }

  /** Organization roles. GET /api/roles/ */
  public getRoles(): Observable<RolesListResponseDto> {
    return this.http.get<RolesListResponseDto>(`${this.apiUrl}/roles/`);
  }

  /** Project roles (admin, member, viewer). GET /api/projectroles/ */
  public getProjectRoles(): Observable<RolesListResponseDto> {
    return this.http.get<RolesListResponseDto>(`${this.apiUrl}/projectroles/`);
  }

  /**
   * Resolves the ProjectMemberRole row (id + current role) for one project member.
   * GET /api/projectmembers/{project_member_id}/role/ — 404 if the member has no role assigned yet.
   */
  public getProjectMemberRole(projectMemberId: string): Observable<ProjectMemberRoleResponseDto> {
    return this.http.get<ProjectMemberRoleResponseDto>(`${this.apiUrl}/projectmembers/${projectMemberId}/role/`);
  }

  /**
   * Changes a member's project role. PATCH /api/projectmemberroles/{id}/ — body: { project_role }.
   * {id} is the ProjectMemberRole id from getProjectMemberRole(), not the project_member_id.
   */
  public updateProjectMemberRole(projectMemberRoleId: string, roleId: string): Observable<ProjectMemberRoleResponseDto> {
    return this.http.patch<ProjectMemberRoleResponseDto>(
      `${this.apiUrl}/projectmemberroles/${projectMemberRoleId}/`,
      { project_role: roleId }
    );
  }

  /** GET /api/projects/{project_id}/link-settings/ — invitation_code, link_join_enabled, default_role_name. */
  public getProjectLinkSettings(projectId: string): Observable<ProjectLinkSettingsResponseDto> {
    return this.http.get<ProjectLinkSettingsResponseDto>(`${this.apiUrl}/projects/${projectId}/link-settings/`);
  }

  /** PATCH /api/projects/{project_id}/link-settings/ — body: { link_join_enabled?, default_role? }. */
  public updateProjectLinkSettings(
    projectId: string,
    body: ProjectLinkSettingsRequestDto
  ): Observable<ProjectLinkSettingsResponseDto> {
    return this.http.patch<ProjectLinkSettingsResponseDto>(`${this.apiUrl}/projects/${projectId}/link-settings/`, body).pipe(
      tap(() => this.projectCache.delete(projectId))
    );
  }

  /** POST /api/projects/{project_id}/link-settings/regenerate/ — invalidates the old invitation_code. */
  public regenerateProjectLink(projectId: string): Observable<ProjectLinkSettingsResponseDto> {
    return this.http.post<ProjectLinkSettingsResponseDto>(`${this.apiUrl}/projects/${projectId}/link-settings/regenerate/`, {});
  }

  /** GET /api/projects/{project_id}/members/detail/ — additive endpoint with role/projects_involved/last_active. */
  public getProjectMembersDetail(projectId: string): Observable<ProjectMembersDetailListResponseDto> {
    return this.http.get<ProjectMembersDetailListResponseDto>(`${this.apiUrl}/projects/${projectId}/members/detail/`);
  }

  /** POST /api/projects/join/ — body: { invitation_code }. Requires an authenticated account. */
  public joinProject(body: JoinProjectRequestDto): Observable<JoinProjectResponseDto> {
    return this.http.post<JoinProjectResponseDto>(`${this.apiUrl}/projects/join/`, body);
  }

}
